import { queryPortDisabledFields } from "../../constants";
import { csvToCargoData } from "./aiImportCsv";
import {
  ALLOWED_FREIGHT_MODES,
  ALLOWED_OPERATIONS,
  ALLOWED_QTY_MODES,
  ALLOWED_TERMS,
} from "./aiImportSchema";

export const validateAIImportCSV = (csvText) => {
  const { cargo, error } = csvToCargoData(csvText);
  if (error) return { isValid: false, error, data: null };
  return validateAIImportCargo(cargo);
};

export const validateAIImportCargo = (cargo) => {
  if (!cargo || typeof cargo !== "object") {
    return { isValid: false, error: "No cargo details found.", data: null };
  }

  // Accept valid fields, ignore/discard invalid ones
  const validCargo = {};

  // Basic query fields
  if (typeof cargo.name === "string") validCargo.name = cargo.name;
  if (typeof cargo.message === "string") validCargo.message = cargo.message;

  if (ALLOWED_FREIGHT_MODES.includes(cargo.freightMode)) validCargo.freightMode = cargo.freightMode;
  if (ALLOWED_QTY_MODES.includes(cargo.qtyMode)) validCargo.qtyMode = cargo.qtyMode;

  if (typeof cargo.suez === "boolean") validCargo.suez = cargo.suez;
  if (typeof cargo.panama === "boolean") validCargo.panama = cargo.panama;

  const toIso = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00.000Z` : v);
  const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  const start = toIso(cargo.laycanStartDate);
  const end = toIso(cargo.laycanEndDate);
  if (start && dateRegex.test(start)) validCargo.laycanStartDate = start;
  if (end && dateRegex.test(end)) validCargo.laycanEndDate = end;

  // Port Operations
  validCargo.portOperations = [];
  if (Array.isArray(cargo.portOperations)) {
    cargo.portOperations.forEach((op) => {
      if (!op || typeof op !== "object") return;

      const validOp = {};

      if (ALLOWED_OPERATIONS.includes(op.operation)) validOp.operation = op.operation;
      if (typeof op.port === "string") validOp.port = op.port;
      if (typeof op.cargoQty === "number") validOp.cargoQty = op.cargoQty;
      if (typeof op.tolerance === "number") validOp.tolerance = op.tolerance;
      if (typeof op.min === "number") validOp.min = op.min;
      if (typeof op.max === "number") validOp.max = op.max;
      if (typeof op.rate === "number") validOp.rate = op.rate;

      if (ALLOWED_TERMS.includes(op.terms)) validOp.terms = op.terms;

      if (typeof op.turnTime === "number") validOp.turnTime = op.turnTime;
      if (typeof op.freight === "number") validOp.freight = op.freight;
      if (typeof op.adcom === "number") validOp.adcom = op.adcom;
      if (typeof op.other === "number") validOp.other = op.other;
      if (typeof op.brokerage === "number") validOp.brokerage = op.brokerage;

      if (typeof op.account === "string") validOp.account = op.account;
      if (typeof op.cargo === "string") validOp.cargo = op.cargo;
      if (typeof op.stowFactor === "number") validOp.stowFactor = op.stowFactor;

      // Discharging rows take these from the Loading row; the form disables them, so don't fill them.
      if (validOp.operation === "Discharging") {
        Object.keys(queryPortDisabledFields.Discharging).forEach((k) => delete validOp[k]);
      }

      // We accept the operation even if it's partially incomplete, user can fix it in the UI
      if (Object.keys(validOp).length > 0) {
        validCargo.portOperations.push(validOp);
      }
    });
  }

  // Ensure we have at least some minimal cargo details to justify an import
  const hasCargoName = typeof validCargo.name === "string" && validCargo.name.trim().length > 0;
  const hasValidPort = Array.isArray(validCargo.portOperations) && validCargo.portOperations.some(op => typeof op.port === "string" && op.port.trim().length > 0);

  if (!hasCargoName && !hasValidPort) {
    return {
      isValid: false,
      error: "The provided file does not contain enough cargo details (such as cargo name or port operations) to import.",
      data: null,
    };
  }

  return {
    isValid: true,
    error: null,
    data: validCargo,
  };
};
